/**
 * Product Storefront v3 Section
 *
 * iter-3d — Collects per-product storefront-v3 fields and writes them all
 * into `inventory_products.extensions.storefront_v3` (JSONB). No DB
 * migration — relies on the existing `extensions` column.
 *
 * Sections collected here:
 *   - Quick facts (duration, group size, weather policy, minimum age,
 *     accessibility, tour type)
 *   - Know before you go (free-text list)
 *   - Meeting point (name, address, lat/lng)
 *   - Tripadvisor (url + aggregate_rating placeholder; sync deferred)
 *
 * Empty fields render nothing on the storefront — the PDP applies the
 * empty-render rule (each module returns null when its data is absent).
 */

import React, { useEffect, useMemo, useRef, useState } from 'react'
import { api } from '@/services/api'
import { useIsSuperAdmin } from '@/hooks/usePermission'

export interface ProductFaqEntry {
  question: string
  answer: string
  isVisible: boolean
  source: 'auto' | 'vendor'
  originalQuestion?: string
  originalAnswer?: string
}

export interface StorefrontV3Extensions {
  duration_minutes?: number
  group_size_min?: number
  group_size_max?: number
  weather_policy?: string
  minimum_age?: number
  accessibility?: string[]
  tour_type?: string
  know_before?: string[]
  faqs?: Array<{
    question: string
    answer: string
    display_order: number
    is_visible: boolean
    source?: 'auto' | 'vendor'
  }>
  meeting_point?: {
    name?: string
    address?: string
    city?: string
    country?: string
    lat?: number
    lng?: number
  }
  tripadvisor_product_url?: string
  // aggregate_rating is set by the future Tripadvisor sync. The PDP
  // hides the block when null.
  aggregate_rating?: {
    rating_value?: number
    review_count?: number
    best_rating?: number
    worst_rating?: number
  } | null
}

interface Props {
  // Read from formData.extensions, write back via the form's setFormData.
  extensions: Record<string, unknown> | undefined
  onChange: (next: Record<string, unknown>) => void
  suggestedFaqs?: Array<{
    question: string
    answer: string
    display_order?: number
    is_visible?: boolean
    source?: 'auto' | 'vendor'
  }>
  faqsAreSuggestedDefaults?: boolean
  // iter-9c: when provided, the FAQ block wires its Save + Regenerate
  // controls to the dedicated PUT/POST /inventory/products/{id}/faqs
  // endpoints so floor enforcement + source promotion happen server-side
  // with audit attribution. Absent on the create-product path - the FAQ
  // block then falls back to writing through extensions only.
  productId?: string
  // iter-9c: floor pinned on the last successful save. Rendered as a chip
  // next to the FAQ header so vendors see the constraint before Save.
  faqsAutoMinimum?: number
}

const ACCESSIBILITY_OPTIONS: Array<{ key: string; label: string }> = [
  { key: 'wheelchair_accessible', label: 'Wheelchair accessible' },
  { key: 'stroller_friendly', label: 'Stroller friendly' },
  { key: 'service_animals_welcome', label: 'Service animals welcome' },
  { key: 'public_transport_nearby', label: 'Public transport nearby' },
  { key: 'audio_described', label: 'Audio described' },
  { key: 'sign_language_available', label: 'Sign language available' },
]

const TOUR_TYPE_OPTIONS = [
  '', 'walking', 'driving', 'cycling', 'boat', 'food', 'cultural',
  'adventure', 'wildlife', 'multi_day', 'workshop', 'other',
]

export default function ProductStorefrontV3Section({
  extensions,
  onChange,
  suggestedFaqs,
  faqsAreSuggestedDefaults = false,
  productId,
  faqsAutoMinimum,
}: Props) {
  const isSuperAdmin = useIsSuperAdmin()
  const [showSuggestedBanner, setShowSuggestedBanner] = useState(faqsAreSuggestedDefaults)
  const hydratedSuggestedFaqs = useRef(false)

  // iter-9c: dedicated FAQ save/regenerate state. Kept local to the FAQ
  // block so the outer product Save button is unaffected.
  const [savingFaqs, setSavingFaqs] = useState(false)
  const [regeneratingFaqs, setRegeneratingFaqs] = useState(false)
  const [faqError, setFaqError] = useState<string | null>(null)
  const [faqSuccess, setFaqSuccess] = useState<string | null>(null)
  const [floor, setFloor] = useState<number>(faqsAutoMinimum ?? 0)

  useEffect(() => {
    if (typeof faqsAutoMinimum === 'number') setFloor(faqsAutoMinimum)
  }, [faqsAutoMinimum])
  // Skip re-hydrating local FAQ drafts when we just wrote extensions ourselves.
  // persistFaqs drops incomplete rows, so a blind sync would erase in-progress entries.
  const extensionsUpdateFromSelf = useRef(false)

  // Current storefront_v3 sub-object inside extensions, or empty.
  const v3 = useMemo<StorefrontV3Extensions>(() => {
    const raw = (extensions as Record<string, unknown> | undefined)?.storefront_v3
    return (raw && typeof raw === 'object' ? (raw as StorefrontV3Extensions) : {}) as StorefrontV3Extensions
  }, [extensions])

  // Write a patch back to extensions.storefront_v3. Drops undefined keys
  // so JSONB stays clean.
  const setV3 = (patch: Partial<StorefrontV3Extensions>) => {
    const next: StorefrontV3Extensions = { ...v3, ...patch }
    const cleaned: Record<string, unknown> = {}
    for (const [k, val] of Object.entries(next)) {
      if (val !== undefined && val !== '' && !(Array.isArray(val) && val.length === 0)) {
        cleaned[k] = val
      }
    }
    extensionsUpdateFromSelf.current = true
    onChange({ ...(extensions || {}), storefront_v3: cleaned })
  }

  const mapStoredFaqs = (): ProductFaqEntry[] =>
    (v3.faqs || []).map((f) => {
      const src: 'auto' | 'vendor' = f.source === 'vendor' ? 'vendor' : 'auto'
      return {
        question: f.question || '',
        answer: f.answer || '',
        isVisible: f.is_visible !== false,
        source: src,
        originalQuestion: src === 'auto' ? f.question || '' : undefined,
        originalAnswer: src === 'auto' ? f.answer || '' : undefined,
      }
    })

  const persistFaqs = (entries: ProductFaqEntry[]) => {
    const cleaned = entries
      .filter((f) => f.question.trim() && f.answer.trim())
      .map((f, i) => {
        const qEdited =
          f.source === 'auto' &&
          (f.originalQuestion ?? '').trim() !== f.question.trim()
        const aEdited =
          f.source === 'auto' &&
          (f.originalAnswer ?? '').trim() !== f.answer.trim()
        const source: 'auto' | 'vendor' =
          f.source === 'vendor' || qEdited || aEdited ? 'vendor' : 'auto'
        return {
          question: f.question.trim(),
          answer: f.answer.trim(),
          display_order: i,
          is_visible: f.isVisible !== false,
          source,
        }
      })
    setV3({ faqs: cleaned.length > 0 ? cleaned : undefined })
    if (entries.length > 0) {
      setShowSuggestedBanner(false)
    }
  }

  const [faqEntries, setFaqEntries] = useState<ProductFaqEntry[]>(() => mapStoredFaqs())

  useEffect(() => {
    if (extensionsUpdateFromSelf.current) {
      extensionsUpdateFromSelf.current = false
      return
    }
    setFaqEntries(mapStoredFaqs())
    setShowSuggestedBanner(faqsAreSuggestedDefaults)
    hydratedSuggestedFaqs.current = false
  }, [extensions, faqsAreSuggestedDefaults])

  useEffect(() => {
    if (!faqsAreSuggestedDefaults || hydratedSuggestedFaqs.current) return
    if ((v3.faqs || []).length > 0) return
    if (!suggestedFaqs?.length) return

    hydratedSuggestedFaqs.current = true
    const next: ProductFaqEntry[] = suggestedFaqs.map((f) => ({
      question: f.question || '',
      answer: f.answer || '',
      isVisible: f.is_visible !== false,
      source: (f.source === 'vendor' ? 'vendor' : 'auto') as 'auto' | 'vendor',
      originalQuestion: f.source === 'vendor' ? undefined : f.question || '',
      originalAnswer: f.source === 'vendor' ? undefined : f.answer || '',
    }))
    setFaqEntries(next)
    persistFaqs(next)
  }, [faqsAreSuggestedDefaults, suggestedFaqs, v3.faqs])

  const updateFaq = (index: number, patch: Partial<ProductFaqEntry>) => {
    const next = [...faqEntries]
    next[index] = { ...next[index], ...patch }
    setFaqEntries(next)
    persistFaqs(next)
  }

  const addFaq = () => {
    if (faqEntries.length >= 20) return
    const next = [
      ...faqEntries,
      { question: '', answer: '', isVisible: true, source: 'vendor' as const },
    ]
    setFaqEntries(next)
  }

  const removeFaq = (index: number) => {
    const next = faqEntries.filter((_, i) => i !== index)
    setFaqEntries(next)
    persistFaqs(next)
  }

  const moveFaq = (index: number, direction: -1 | 1) => {
    const target = index + direction
    if (target < 0 || target >= faqEntries.length) return
    const next = [...faqEntries]
    ;[next[index], next[target]] = [next[target], next[index]]
    setFaqEntries(next)
    persistFaqs(next)
  }

  const visibleFaqCount = faqEntries.filter((f) => f.isVisible).length

  // iter-9c: server-authoritative save. Counts auto entries the same way
  // the API does (source stays 'auto' only when text is unchanged from the
  // stored original at that display_order). Blocks Save when the client
  // can already see the floor would be breached, saving a 422 round-trip.
  const autoEntriesAfterSave = faqEntries.filter((f) => {
    if (f.source !== 'auto') return false
    const qEdited = (f.originalQuestion ?? '').trim() !== f.question.trim()
    const aEdited = (f.originalAnswer ?? '').trim() !== f.answer.trim()
    return !(qEdited || aEdited)
  }).length

  const wouldBreachFloor = autoEntriesAfterSave < floor

  const handleSaveFaqs = async () => {
    if (!productId) return

    setFaqError(null)
    setFaqSuccess(null)
    setSavingFaqs(true)

    try {
      const cleaned = faqEntries
        .filter((f) => f.question.trim() && f.answer.trim())
        .map((f, i) => ({
          question: f.question.trim(),
          answer: f.answer.trim(),
          displayOrder: i,
          isVisible: f.isVisible !== false,
        }))

      const visibleCount = cleaned.filter((f) => f.isVisible).length
      if (visibleCount > 12) {
        setFaqError('Up to 12 FAQs may be visible on the product page. Hide some to continue.')
        setSavingFaqs(false)
        return
      }

      const res = await api.inventory.updateFaqs(productId, { faqs: cleaned })
      const payload = res.data?.data || res.data || {}
      const saved = (payload.faqs || []) as Array<{
        question: string
        answer: string
        display_order: number
        is_visible: boolean
        source?: 'auto' | 'vendor'
      }>
      const nextFloor =
        typeof payload.faqs_auto_minimum === 'number' ? payload.faqs_auto_minimum : floor
      setFloor(nextFloor)

      // Rebuild local state from the server response so source badges and
      // originalQuestion/Answer stamps reflect the freshly-resolved tags.
      const rebuilt: ProductFaqEntry[] = saved.map((f) => {
        const src: 'auto' | 'vendor' = f.source === 'vendor' ? 'vendor' : 'auto'
        return {
          question: f.question || '',
          answer: f.answer || '',
          isVisible: f.is_visible !== false,
          source: src,
          originalQuestion: src === 'auto' ? f.question || '' : undefined,
          originalAnswer: src === 'auto' ? f.answer || '' : undefined,
        }
      })
      setFaqEntries(rebuilt)
      setShowSuggestedBanner(false)
      // Mirror the server state back into extensions so the outer form's
      // dirty tracking + preview reflect what actually persisted.
      const persisted = rebuilt.map((f, i) => ({
        question: f.question,
        answer: f.answer,
        display_order: i,
        is_visible: f.isVisible !== false,
        source: f.source,
      }))
      setV3({ faqs: persisted.length > 0 ? persisted : undefined })
      setFaqSuccess('Product FAQs saved')
    } catch (err: any) {
      const status = err.response?.status
      const body = err.response?.data
      if (status === 422 && body?.details) {
        const { floor: fl, autoEntryCount, visibleAutoCount } = body.details as {
          floor?: number
          autoEntryCount?: number
          visibleAutoCount?: number
        }
        setFaqError(
          `You cannot drop below ${fl ?? floor} auto-generated FAQs. Currently keeping ${autoEntryCount ?? 0} (${visibleAutoCount ?? 0} visible). Hide entries instead of deleting them, or ask an admin to regenerate.`,
        )
      } else {
        const message =
          body?.error?.message || body?.message || err.message || 'Failed to save product FAQs'
        setFaqError(message)
      }
    } finally {
      setSavingFaqs(false)
    }
  }

  const handleRegenerateFaqs = async () => {
    if (!productId) return
    if (
      !window.confirm(
        'Regenerate product FAQs from the current product data? Vendor-edited entries are preserved; only the auto-generated set is replaced. Floor ratchets up if there are now more auto entries.',
      )
    ) {
      return
    }

    setFaqError(null)
    setFaqSuccess(null)
    setRegeneratingFaqs(true)

    try {
      const res = await api.inventory.regenerateFaqs(productId)
      const payload = res.data?.data || res.data || {}
      const fresh = (payload.faqs || []) as Array<{
        question: string
        answer: string
        display_order: number
        is_visible: boolean
        source?: 'auto' | 'vendor'
      }>
      const nextFloor =
        typeof payload.faqs_auto_minimum === 'number' ? payload.faqs_auto_minimum : floor
      setFloor(nextFloor)

      const rebuilt: ProductFaqEntry[] = fresh.map((f) => {
        const src: 'auto' | 'vendor' = f.source === 'vendor' ? 'vendor' : 'auto'
        return {
          question: f.question || '',
          answer: f.answer || '',
          isVisible: f.is_visible !== false,
          source: src,
          originalQuestion: src === 'auto' ? f.question || '' : undefined,
          originalAnswer: src === 'auto' ? f.answer || '' : undefined,
        }
      })
      setFaqEntries(rebuilt)
      setShowSuggestedBanner(false)
      const persisted = rebuilt.map((f, i) => ({
        question: f.question,
        answer: f.answer,
        display_order: i,
        is_visible: f.isVisible !== false,
        source: f.source,
      }))
      setV3({ faqs: persisted.length > 0 ? persisted : undefined })
      setFaqSuccess('Product FAQs regenerated')
    } catch (err: any) {
      const message =
        err.response?.data?.error?.message ||
        err.response?.data?.message ||
        err.message ||
        'Failed to regenerate product FAQs'
      setFaqError(message)
    } finally {
      setRegeneratingFaqs(false)
    }
  }

  const setMeetingPoint = (patch: Partial<NonNullable<StorefrontV3Extensions['meeting_point']>>) => {
    const next = { ...(v3.meeting_point || {}), ...patch }
    const cleaned: Record<string, unknown> = {}
    for (const [k, val] of Object.entries(next)) {
      if (val !== undefined && val !== '' && !Number.isNaN(val)) {
        cleaned[k] = val
      }
    }
    setV3({ meeting_point: Object.keys(cleaned).length ? cleaned : undefined })
  }

  const toggleAccessibility = (key: string) => {
    const current = v3.accessibility || []
    const next = current.includes(key)
      ? current.filter((k) => k !== key)
      : [...current, key]
    setV3({ accessibility: next })
  }

  // Know-before list editing.
  const updateKnowBefore = (idx: number, value: string) => {
    const next = [...(v3.know_before || [])]
    next[idx] = value
    setV3({ know_before: next })
  }
  const addKnowBefore = () => {
    if ((v3.know_before || []).length >= 30) return
    setV3({ know_before: [...(v3.know_before || []), ''] })
  }
  const removeKnowBefore = (idx: number) => {
    const next = (v3.know_before || []).filter((_, i) => i !== idx)
    setV3({ know_before: next })
  }

  const rating = v3.aggregate_rating || null

  return (
    <div style={containerStyle}>
      <h3 style={sectionTitleStyle}>Storefront v3 fields</h3>
      <p style={hintStyle}>
        Used by the public storefront PDP to render Quick facts, Know
        before you go, the meeting point map, and Tripadvisor pull-through.
        Anything left blank is hidden rather than guessed.
      </p>

      {/* Quick facts */}
      <h4 style={subheadStyle}>Quick facts</h4>
      <div style={gridTwoColStyle}>
        <div>
          <label style={labelStyle}>Duration (minutes)</label>
          <input
            type="number"
            min={0}
            max={10000}
            value={v3.duration_minutes ?? ''}
            onChange={(e) =>
              setV3({ duration_minutes: e.target.value === '' ? undefined : parseInt(e.target.value, 10) })
            }
            style={inputStyle}
            placeholder="e.g. 90"
          />
        </div>

        <div>
          <label style={labelStyle}>Tour type</label>
          <select
            value={v3.tour_type || ''}
            onChange={(e) => setV3({ tour_type: e.target.value || undefined })}
            style={inputStyle}
          >
            {TOUR_TYPE_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>{opt || '— select —'}</option>
            ))}
          </select>
        </div>

        <div>
          <label style={labelStyle}>Group size — minimum</label>
          <input
            type="number"
            min={1}
            max={500}
            value={v3.group_size_min ?? ''}
            onChange={(e) =>
              setV3({ group_size_min: e.target.value === '' ? undefined : parseInt(e.target.value, 10) })
            }
            style={inputStyle}
          />
        </div>

        <div>
          <label style={labelStyle}>Group size — maximum</label>
          <input
            type="number"
            min={1}
            max={500}
            value={v3.group_size_max ?? ''}
            onChange={(e) =>
              setV3({ group_size_max: e.target.value === '' ? undefined : parseInt(e.target.value, 10) })
            }
            style={inputStyle}
          />
        </div>

        <div>
          <label style={labelStyle}>Minimum age</label>
          <input
            type="number"
            min={0}
            max={120}
            value={v3.minimum_age ?? ''}
            onChange={(e) =>
              setV3({ minimum_age: e.target.value === '' ? undefined : parseInt(e.target.value, 10) })
            }
            style={inputStyle}
            placeholder="0 means no minimum"
          />
        </div>

        <div>
          <label style={labelStyle}>Weather policy</label>
          <input
            type="text"
            value={v3.weather_policy || ''}
            onChange={(e) => setV3({ weather_policy: e.target.value || undefined })}
            maxLength={300}
            style={inputStyle}
            placeholder="e.g. Runs rain or shine; full refund on lightning"
          />
        </div>
      </div>

      <div style={{ marginTop: '0.75rem' }}>
        <label style={labelStyle}>Accessibility</label>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.35rem' }}>
          {ACCESSIBILITY_OPTIONS.map((opt) => (
            <label key={opt.key} style={checkboxLabelStyle}>
              <input
                type="checkbox"
                checked={(v3.accessibility || []).includes(opt.key)}
                onChange={() => toggleAccessibility(opt.key)}
              />
              <span>{opt.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Know before */}
      <h4 style={subheadStyle}>Know before you go</h4>
      <p style={hintStyle}>Short bullets shown on the PDP. Up to 30 items.</p>
      <div style={{ display: 'grid', gap: '0.5rem' }}>
        {(v3.know_before || []).map((item, idx) => (
          <div key={idx} style={{ display: 'flex', gap: '0.5rem' }}>
            <input
              type="text"
              value={item}
              onChange={(e) => updateKnowBefore(idx, e.target.value)}
              maxLength={300}
              style={{ ...inputStyle, flex: 1 }}
              placeholder="e.g. Wear comfortable walking shoes"
            />
            <button type="button" onClick={() => removeKnowBefore(idx)} style={miniButtonStyle}>Remove</button>
          </div>
        ))}
        <button
          type="button"
          onClick={addKnowBefore}
          disabled={(v3.know_before || []).length >= 30}
          style={{ ...miniButtonStyle, width: 'fit-content' }}
        >
          {(v3.know_before || []).length >= 30 ? 'Limit reached' : 'Add item'}
        </button>
      </div>

      {/* Meeting point */}
      <h4 style={subheadStyle}>Meeting point</h4>
      <div style={gridTwoColStyle}>
        <div>
          <label style={labelStyle}>Name</label>
          <input
            type="text"
            value={v3.meeting_point?.name || ''}
            onChange={(e) => setMeetingPoint({ name: e.target.value || undefined })}
            maxLength={120}
            style={inputStyle}
            placeholder='e.g. "Outside Spitalfields Market, main entrance"'
          />
        </div>
        <div>
          <label style={labelStyle}>City</label>
          <input
            type="text"
            value={v3.meeting_point?.city || ''}
            onChange={(e) => setMeetingPoint({ city: e.target.value || undefined })}
            maxLength={80}
            style={inputStyle}
          />
        </div>
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={labelStyle}>Address</label>
          <input
            type="text"
            value={v3.meeting_point?.address || ''}
            onChange={(e) => setMeetingPoint({ address: e.target.value || undefined })}
            maxLength={240}
            style={inputStyle}
          />
        </div>
        <div>
          <label style={labelStyle}>Country</label>
          <input
            type="text"
            value={v3.meeting_point?.country || ''}
            onChange={(e) => setMeetingPoint({ country: e.target.value || undefined })}
            maxLength={80}
            style={inputStyle}
            placeholder="e.g. United Kingdom"
          />
        </div>
        <div></div>
        <div>
          <label style={labelStyle}>Latitude</label>
          <input
            type="number"
            step="0.000001"
            min={-90}
            max={90}
            value={v3.meeting_point?.lat ?? ''}
            onChange={(e) =>
              setMeetingPoint({ lat: e.target.value === '' ? undefined : parseFloat(e.target.value) })
            }
            style={inputStyle}
          />
        </div>
        <div>
          <label style={labelStyle}>Longitude</label>
          <input
            type="number"
            step="0.000001"
            min={-180}
            max={180}
            value={v3.meeting_point?.lng ?? ''}
            onChange={(e) =>
              setMeetingPoint({ lng: e.target.value === '' ? undefined : parseFloat(e.target.value) })
            }
            style={inputStyle}
          />
        </div>
      </div>

      {/* Tripadvisor */}
      <h4 style={subheadStyle}>Tripadvisor</h4>
      <p style={hintStyle}>
        URL is wired through for the PDP. Aggregate rating syncs once the
        Tripadvisor integration ships; the PDP hides the rating block
        when null.
      </p>
      <div>
        <label style={labelStyle}>Tripadvisor product URL</label>
        <input
          type="text"
          value={v3.tripadvisor_product_url || ''}
          onChange={(e) => setV3({ tripadvisor_product_url: e.target.value || undefined })}
          maxLength={2048}
          style={inputStyle}
          placeholder="https://www.tripadvisor.com/Attraction_Review-..."
        />
      </div>

      {rating && (
        <p style={{ ...hintStyle, marginTop: '0.5rem' }}>
          Synced rating: {rating.rating_value ?? '—'} from {rating.review_count ?? 0} reviews.
        </p>
      )}

      {/* Product FAQs — same defaults as the public PDP */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
        <h4 style={{ ...subheadStyle, margin: 0 }}>Product FAQs</h4>
        {floor > 0 && (
          <span
            title="Server-enforced minimum on the number of auto-generated FAQ entries that must remain. Hide entries instead of deleting to stay above the floor."
            style={{
              fontSize: '0.7rem',
              padding: '0.15rem 0.5rem',
              borderRadius: '999px',
              background: 'var(--color-gray-100)',
              color: 'var(--color-gray-700)',
              fontWeight: 500,
            }}
          >
            Auto-FAQ floor: {floor}
          </span>
        )}
      </div>
      <p style={hintStyle}>
        Shown on this experience&apos;s public product page and in FAQPage
        structured data. Up to 20 entries; 12 may be visible at once.
      </p>

      {showSuggestedBanner && faqEntries.length > 0 && (
        <p
          style={{
            ...hintStyle,
            padding: '0.65rem 0.75rem',
            background: 'var(--color-blue-50, #eff6ff)',
            borderRadius: '6px',
            color: 'var(--color-tixxly-navy)',
          }}
        >
          These FAQs match what visitors see on this product page today. Edit
          them here, then save the experience to keep your changes.
        </p>
      )}

      <div style={{ display: 'grid', gap: '0.75rem' }}>
        {faqEntries.map((faq, idx) => {
          const qEdited =
            faq.source === 'auto' &&
            (faq.originalQuestion ?? '').trim() !== faq.question.trim()
          const aEdited =
            faq.source === 'auto' &&
            (faq.originalAnswer ?? '').trim() !== faq.answer.trim()
          const wasAutoEdited = qEdited || aEdited

          let badgeLabel: string
          let badgeColor: string
          let badgeBg: string
          if (faq.source === 'vendor') {
            badgeLabel = 'Vendor-added'
            badgeColor = 'var(--color-tixxly-navy)'
            badgeBg = 'var(--color-blue-50, #eff6ff)'
          } else if (wasAutoEdited) {
            badgeLabel = 'Edited (was auto-generated)'
            badgeColor = 'var(--color-orange-700, #c2410c)'
            badgeBg = 'var(--color-orange-50, #fff7ed)'
          } else {
            badgeLabel = 'Auto-generated'
            badgeColor = 'var(--color-gray-700)'
            badgeBg = 'var(--color-gray-100)'
          }

          return (
            <div key={idx} style={faqCardStyle}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <strong style={{ fontSize: '0.85rem' }}>FAQ #{idx + 1}</strong>
                  <span
                    style={{
                      fontSize: '0.7rem',
                      padding: '0.15rem 0.45rem',
                      borderRadius: '999px',
                      background: badgeBg,
                      color: badgeColor,
                      fontWeight: 500,
                    }}
                  >
                    {badgeLabel}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: '0.25rem' }}>
                  <button type="button" onClick={() => moveFaq(idx, -1)} disabled={idx === 0} style={miniButtonStyle}>↑</button>
                  <button type="button" onClick={() => moveFaq(idx, 1)} disabled={idx === faqEntries.length - 1} style={miniButtonStyle}>↓</button>
                  <button type="button" onClick={() => removeFaq(idx)} style={{ ...miniButtonStyle, color: 'var(--color-red-600, #b91c1c)' }}>Remove</button>
                </div>
              </div>

              <div style={{ display: 'grid', gap: '0.5rem' }}>
                <input
                  type="text"
                  value={faq.question}
                  onChange={(e) => updateFaq(idx, { question: e.target.value })}
                  maxLength={240}
                  style={inputStyle}
                  placeholder="Question"
                />
                <textarea
                  value={faq.answer}
                  onChange={(e) => updateFaq(idx, { answer: e.target.value })}
                  maxLength={1500}
                  rows={3}
                  style={textareaStyle}
                  placeholder="Answer"
                />
                <label style={{ ...checkboxLabelStyle, fontSize: '0.8rem' }}>
                  <input
                    type="checkbox"
                    checked={faq.isVisible}
                    onChange={(e) => updateFaq(idx, { isVisible: e.target.checked })}
                  />
                  <span>Visible on product page</span>
                </label>
              </div>
            </div>
          )
        })}

        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <button
            type="button"
            onClick={addFaq}
            disabled={faqEntries.length >= 20}
            style={miniButtonStyle}
          >
            {faqEntries.length >= 20 ? 'Limit reached' : 'Add FAQ'}
          </button>
          <span style={hintStyle}>
            {faqEntries.length} / 20 total · {visibleFaqCount} / 12 visible
          </span>
        </div>

        {/* iter-9c: dedicated FAQ save + regenerate. Only rendered on the
            edit-product path (productId known). Create path continues to
            persist FAQs via the outer form's product save. */}
        {productId && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.5rem',
              marginTop: '0.5rem',
              paddingTop: '0.75rem',
              borderTop: '1px solid var(--color-gray-200)',
            }}
          >
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
              <button
                type="button"
                onClick={handleSaveFaqs}
                disabled={savingFaqs || regeneratingFaqs || wouldBreachFloor}
                title={
                  wouldBreachFloor
                    ? `Would drop below the auto-FAQ floor of ${floor}. Hide entries instead of deleting them.`
                    : undefined
                }
                style={primaryButtonStyle}
              >
                {savingFaqs ? 'Saving…' : 'Save FAQs'}
              </button>
              {isSuperAdmin && (
                <button
                  type="button"
                  onClick={handleRegenerateFaqs}
                  disabled={savingFaqs || regeneratingFaqs}
                  style={secondaryButtonStyle}
                >
                  {regeneratingFaqs ? 'Regenerating…' : 'Regenerate (admin)'}
                </button>
              )}
              <span style={hintStyle}>
                Saves through the dedicated FAQ endpoint so the auto-floor
                and vendor-edit tags are enforced server-side.
              </span>
            </div>
            {faqError && (
              <p
                role="alert"
                style={{
                  margin: 0,
                  fontSize: '0.8rem',
                  color: 'var(--color-red-700, #b91c1c)',
                  background: 'var(--color-red-50, #fef2f2)',
                  border: '1px solid var(--color-red-200, #fecaca)',
                  borderRadius: '6px',
                  padding: '0.5rem 0.65rem',
                }}
              >
                {faqError}
              </p>
            )}
            {faqSuccess && (
              <p
                role="status"
                style={{
                  margin: 0,
                  fontSize: '0.8rem',
                  color: 'var(--color-green-700, #15803d)',
                  background: 'var(--color-green-50, #f0fdf4)',
                  border: '1px solid var(--color-green-200, #bbf7d0)',
                  borderRadius: '6px',
                  padding: '0.5rem 0.65rem',
                }}
              >
                {faqSuccess}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

const primaryButtonStyle: React.CSSProperties = {
  padding: '0.5rem 0.9rem',
  fontSize: '0.85rem',
  fontWeight: 500,
  borderRadius: '6px',
  border: '1px solid var(--color-tixxly-navy)',
  background: 'var(--color-tixxly-navy)',
  color: 'white',
  cursor: 'pointer',
}

const secondaryButtonStyle: React.CSSProperties = {
  padding: '0.5rem 0.9rem',
  fontSize: '0.85rem',
  fontWeight: 500,
  borderRadius: '6px',
  border: '1px solid var(--color-gray-300)',
  background: 'white',
  color: 'var(--color-gray-800)',
  cursor: 'pointer',
}

const containerStyle: React.CSSProperties = {
  marginTop: '1.5rem',
  padding: '1.25rem',
  background: 'var(--color-gray-50)',
  border: '1px solid var(--color-gray-200)',
  borderRadius: '8px',
  display: 'grid',
  gap: '0.5rem',
}

const sectionTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: '1rem',
  fontWeight: 600,
  color: 'var(--color-gray-900)',
}

const subheadStyle: React.CSSProperties = {
  marginTop: '1rem',
  marginBottom: '0.5rem',
  fontSize: '0.9rem',
  fontWeight: 600,
  color: 'var(--color-gray-800)',
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  marginBottom: '0.3rem',
  fontSize: '0.8rem',
  fontWeight: 500,
  color: 'var(--color-gray-700)',
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.45rem 0.65rem',
  border: '1px solid var(--color-gray-300)',
  borderRadius: '6px',
  fontSize: '0.85rem',
  background: 'white',
}

const hintStyle: React.CSSProperties = {
  fontSize: '0.75rem',
  color: 'var(--color-gray-500)',
  margin: '0.25rem 0',
}

const gridTwoColStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gap: '0.75rem',
}

const checkboxLabelStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.4rem',
  fontSize: '0.8rem',
  color: 'var(--color-gray-700)',
  cursor: 'pointer',
}

const miniButtonStyle: React.CSSProperties = {
  background: 'var(--color-gray-100)',
  border: '1px solid var(--color-gray-300)',
  borderRadius: '4px',
  padding: '0.3rem 0.6rem',
  fontSize: '0.75rem',
  cursor: 'pointer',
}

const faqCardStyle: React.CSSProperties = {
  padding: '0.75rem',
  background: 'white',
  borderRadius: '6px',
  border: '1px solid var(--color-gray-200)',
}

const textareaStyle: React.CSSProperties = {
  ...inputStyle,
  fontFamily: 'inherit',
  resize: 'vertical',
}
